import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, AlertCircle, ArrowUp } from 'lucide-react';

export default function TierElevationDialog({ open, onOpenChange, ticket, onConfirm, submitting }) {
  const currentTier = ticket?.assignment_tier || 1;
  const defaultTarget = String(Math.min(currentTier + 1, 3));
  const [targetTier, setTargetTier] = useState(defaultTarget);
  const [priority, setPriority] = useState(ticket?.priority || 'high');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setTargetTier(String(Math.min((ticket?.assignment_tier || 1) + 1, 3)));
      setPriority(ticket?.priority === 'urgent' ? 'urgent' : 'high');
      setReason('');
    }
  }, [open, ticket]);

  const tierOptions = [];
  if (currentTier < 2) tierOptions.push({ value: '2', label: 'Tier 2 — Advanced' });
  if (currentTier < 3) tierOptions.push({ value: '3', label: 'Tier 3 — Senior' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!targetTier) return;
    onConfirm?.({
      target_tier: parseInt(targetTier, 10),
      priority,
      reason: reason.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 max-w-md" data-testid="tier-elevation-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-800 flex items-center gap-2">
            <ArrowUp className="w-5 h-5 text-amber-500" />Request Tier Elevation
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            This ticket is currently at <strong>Tier {currentTier}</strong>. Choose the tier you want to escalate it to and optionally bump priority.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {tierOptions.length === 0 ? (
            <p className="text-sm text-slate-500 p-3 rounded-lg bg-slate-50 border border-slate-200">Already at the highest tier.</p>
          ) : (
            <div className="space-y-2">
              <Label className="text-slate-700">Escalate to</Label>
              <RadioGroup value={targetTier} onValueChange={setTargetTier} className="grid grid-cols-1 gap-2">
                {tierOptions.map((opt) => (
                  <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${targetTier === opt.value ? 'border-cyan-500 bg-cyan-50/50' : 'border-slate-200 hover:bg-slate-50'}`}>
                    <RadioGroupItem value={opt.value} id={`tier-${opt.value}`} data-testid={`tier-elev-${opt.value}`} />
                    <span className="text-sm font-medium text-slate-800">{opt.label}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-slate-700 flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4 text-amber-500" />Mark priority as
            </Label>
            <RadioGroup value={priority} onValueChange={setPriority} className="grid grid-cols-2 gap-2">
              {[
                { v: 'high', label: 'High', tone: 'border-orange-300 text-orange-700 bg-orange-50' },
                { v: 'urgent', label: 'Urgent / Critical', tone: 'border-red-300 text-red-700 bg-red-50' },
              ].map((opt) => (
                <label key={opt.v} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${priority === opt.v ? `${opt.tone} font-semibold` : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
                  <RadioGroupItem value={opt.v} id={`prio-${opt.v}`} data-testid={`tier-prio-${opt.v}`} />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="elev-reason" className="text-slate-700">Reason <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea
              id="elev-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why does this ticket need a higher tier?"
              className="bg-white border-slate-200 min-h-[70px]"
              data-testid="tier-elev-reason"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-600">Cancel</Button>
            <Button type="submit" disabled={submitting || tierOptions.length === 0} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="tier-elev-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Request elevation'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
